import React, { useState, useEffect } from 'react';
import AgentsTable from './tables/AgentsTable';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
    tryToDeleteAgent,
    tryToDisableAgent,
    tryToEnableAgent,
    tryToFetchAgents
} from '../../../store/brokers/partner/agents/agentsSlice';
import { IToast } from '../../../interfaces/components/IToast';
import { IPaginationPayload } from '../../../interfaces/shared/IPaginationPayload';
import AgentDetailsModal from './modals/AgentDetailsModal';
import AgentManageModal from './modals/AgentManageModal';
import EmptyContainer from '../../../shared/empty-container';
import DeleteModal from '../../../shared/delete-modal';
import Button from '../../../shared/button';
import Toast from '../../../shared/toast';
import Input from '../../../shared/input';


const Agents = () => {
    const dispatch = useAppDispatch();
    const [rows, setRows] = useState<any>([]);
    const [search, setSearch] = useState<string>();
    const [showToast, setShowToast] = useState<IToast | null>()
    const [paginationState, setPaginationState] = useState<IPaginationPayload>({
        pageNumber: 1,
        pageSize: 10,
    });
    const [showDetailsModal, setShowDetailsModal] = useState<{ show: boolean, data: any }>()
    const [showManageModal, setShowManageModal] = useState<{ show: boolean, id?: string, data?: any }>()
    const [showDeleteModal, setShowDeleteModal] = useState<{ show: boolean, id: string }>()
    const paginationData = useAppSelector((state) => state.agents.agents?.data?.page);
    const rowsLoading = useAppSelector((state) => state.agents.agentsAreLoading);
    const state = useAppSelector((state) => state.agents);


    const onResetPaginationState = () => {
        setPaginationState({
            pageNumber: 1,
            pageSize: paginationState?.pageSize || 10,
        })
    }

    const onChangeSearchValue = (e: any) => {
        setSearch(e)
        onResetPaginationState()
    }

    useEffect(() => {
        if (state.agents) {
            const rows =
                state.agents?.data?.elements && Array.isArray(state.agents?.data.elements)
                    ? state.agents?.data.elements.map((r: any) => ({ ...r, id: r._id }))
                    : [];
            setRows(rows);
        }
    }, [state.agents]);

    const onGettingAgentData = async () => {
        try {
            let data: any = { ...paginationState }
            if(search){
                data = {
                    ...data || {},
                    filters: search
                }
            }
            await dispatch(tryToFetchAgents(data)).unwrap();
        } catch (err) {
            // err
        }
    }

    useEffect(() => {
        onGettingAgentData();
    }, [paginationState]);

    const onSeeAgentDetails = async (data: any) => {
        setShowDetailsModal({
            show: true,
            data: data
        })
        document.body.style.overflow = 'hidden';
    }

    const onSeeManageAgentModal = async (id?: string, data?: any) => {
        setShowManageModal({
            show: true,
            id: id,
            data: data
        })
        document.body.style.overflow = 'hidden';
    }

    const onEnableDisableAgent = async (id: string, currentStatus: boolean) => {
        try {
            if (currentStatus) {
                await dispatch(tryToDisableAgent(id)).unwrap();
                setShowToast({ type: 'success', message: `Member disabled successfully` });
            } else {
                await dispatch(tryToEnableAgent(id)).unwrap();
                setShowToast({ type: 'success', message: `Member enabled successfully` });
            }
        } catch (error) {
            setShowToast({ type: 'error', message: `${error}` });
        }
    }

    const onDeleteAgent = async () => {
        try {
            await dispatch(tryToDeleteAgent(showDeleteModal?.id || ''))
            setShowToast({ type: 'success', message: `Member successfully deleted` });
        } catch (error) {
            setShowToast({ type: 'error', message: `${error}` });
        }
        setShowDeleteModal(undefined)
    }

    const onRequestDeleteAgent = (id: string) => {
        setShowDeleteModal({ show: true, id })
    }

    const onCloseDeleteModal = () => {
        setShowDeleteModal(undefined)
        document.body.style.overflow = 'auto';
    }

    const onSaveChanges = (type: string, message: string) => {
        setShowToast({ type, message })
    }

    const onCloseModal = () => {
        setShowDetailsModal(undefined)
        setShowManageModal(undefined)
        document.body.style.overflow = 'auto';
    }

    return (
        <div>
            <div>
                <p className='page-title'>Members</p>
            </div>
            <div className='flex flex-row justify-end'>
                <Button
                    label={'Add New Member'}
                    dataQa={'add-new-member'}
                    className={`btn-primary-rounded-less`}
                    onClickButton={() => onSeeManageAgentModal()}
                />
            </div>
            <div className='my-5 relative'>
                <div>
                    <Input
                        placeholder={'Search for Members'}
                        dataQa={'search'}
                        containerStyle='mb-0 pb-0 relative min-w-[400px]'
                        inputStyle={`mb-0 border-0 focus:outline-none`}
                        icon={'/assets/shared/search.svg'}
                        showValue={true}
                        inputValue={search}
                        inputUniqueName={'search'}
                        onChangeInput={onChangeSearchValue}
                        hideInputSpace={true}
                    />
                </div>
                {(rows?.length > 0 || rowsLoading) ?
                    <AgentsTable
                        rows={rows}
                        rowsLoading={rowsLoading}
                        onSeeAgentDetails={onSeeAgentDetails}
                        onEnableDisableAgent={onEnableDisableAgent}
                        paginationData={paginationData}
                        onEditAgent={onSeeManageAgentModal}
                        setPaginationState={setPaginationState}
                        onDeleteAgent={onRequestDeleteAgent}
                    />
                    : <EmptyContainer text={'No member added yet.'} showImage={true} />
                }
            </div>
            {showToast?.message &&
                <Toast
                    type={showToast?.type}
                    text={showToast?.message}
                    onHandleCloseToast={() => setShowToast(null)}
                />
            }
            <DeleteModal
                openDeleteModal={showDeleteModal?.show || false}
                handleCloseDeleteModal={onCloseDeleteModal}
                onApproveDeletion={onDeleteAgent}
            />
            {showDetailsModal?.show &&
                <AgentDetailsModal
                    openModal={showDetailsModal?.show}
                    data={showDetailsModal?.data}
                    handleCloseModal={onCloseModal}
                />
            }
            {showManageModal?.show &&
                <AgentManageModal
                    openModal={showManageModal?.show}
                    data={showManageModal?.data}
                    id={showManageModal?.id}
                    handleCloseModal={onCloseModal}
                    onSuccessSaved={onSaveChanges}
                />
            }
        </div>
    )
}

export default Agents;